import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { MultiSelect } from 'react-multi-select-component';
import { listShippingServices } from '../../modules/plans';
import { PageTitle } from '../../../_metronic/layout/core';
import {
  OrderList,
  OrderModel,
  OrderOriginEnum,
  OrderStateEnum,
  ORIGIN_LABEL_IDS,
  createOrder,
  listOrders,
  updateOrder,
  SaleableProductModel,
  ModalOrderForm,
  listSaleableProducts,
  ModalOrderLines,
  OrderLineModel,
  createOrderLine,
  removeOrderLine,
  updateOrderLine,
  ModalEdiForm,
  BulkActionsEnum,
  BulkActionConfig,
  performBulkAction,
  retrieveOrder,
} from '../../modules/b2b-orders';
import {
  DistributorModel,
  listDistributors,
  searchDistributorShippingAddresses,
} from '../../modules/distributors';
import { ModalInvoiceForm, InvoiceKind } from '../../modules/b2b-invoices';
import {
  DeliveryNoteLineRequestModel,
  ModalDeliveryNoteForm,
  bulkInsertDeliveryNoteLines,
  createDeliveryNote,
} from '../../modules/delivery-notes';
import ModalComments from '../../modules/b2b-orders/components/ModalComments';
import COUNTRIES from '../../constants/countries';
import COUNTRY_NAMES from '../../constants/countryNames';
import DateRangeSelector from '../../components/DateRangeSelector';
import { Warehouse } from '../../modules/stock/models/StockLine';
import WarehouseFilter from '../../components/WarehouseFilter';
import getMultiSelectStrings from '../../constants/getMultiSelectStrings';
import { getApiErrorMessage } from '../../utils/getApiErrorMessage';

const DEBOUNCE_TIMEOUT = 800;
const ANY_SERVICE = 'ANY_SERVICE';
const EMPTY_ORDER: Partial<OrderModel> = {
  shipping_fee: 0.0,
  total_discounts: 0.0,
  promise_date: null,
  shipping_address: null,
  billing_address: null,
  distributor: null,
  discount_mode: 'absolute',
  discount_on: 'order',
};

const BULK_ACTIONS: BulkActionConfig[] = [
  {
    action: BulkActionsEnum.EXPORT_ORDERS,
    i18nKey: 'exportOrders',
    icon: 'fa-file-export',
  },
  {
    action: BulkActionsEnum.EXPORT_COMERCIAL_ORDERS,
    i18nKey: 'exportComercialOrders',
    icon: 'fa-file-export',
  },
  {
    action: BulkActionsEnum.GENERATE_WAREHOUSE_OPERATIONS_SHEET,
    i18nKey: 'generateWarehouseOperationsSheet',
    icon: 'fa-list',
  },
  {
    action: BulkActionsEnum.GENERATE_PRODUCTS_NECESSITY_SHEET,
    i18nKey: 'generateProductsNecessitySheet',
    icon: 'fa-list-alt',
  },
  {
    action: BulkActionsEnum.PREPARE_ORDERS,
    i18nKey: 'prepareOrders',
    icon: 'fa-magic',
  },
  {
    action: BulkActionsEnum.DOWNLOAD_RUTERO_FILE,
    i18nKey: 'downloadRuteroFile',
    icon: 'fa-download',
  },
];

const B2BOrdersPage: FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const [shippingServices, setShippingServices] = useState([]);
  const [visibleCommentsModal, setVisibleCommentsModal] = useState(false);
  const [visibleOrderModal, setVisibleOrderModal] = useState(false);
  const [visibleParseEdiModal, setVisibleParseEdiModal] = useState(false);
  const [visibleLinesModal, setVisibleLinesModal] = useState(false);
  const [visibleInvoiceModal, setVisibleInvoiceModal] = useState(false);
  const [visibleDeliveryNoteModal, setVisibleDeliveryNoteModal] =
    useState(false);
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [order, setOrder] = useState<OrderModel | undefined>(undefined);
  const [distributors, setDistributors] = useState([]);
  const [products, setProducts] = useState<Array<SaleableProductModel>>([]);
  const [orders, setOrders] = useState<Array<OrderModel>>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedShippingService, setSelectedShippingService] = useState(null);
  const [selectedDistributor, setSelectedDistributor] =
    useState<DistributorModel | null>(null);
  const [selectedStates, setSelectedStates] = useState<Array<OrderStateEnum>>(
    []
  );
  const [promiseDateRange, setPromiseDateRange] = useState<{
    since: Date | null;
    until: Date | null;
  }>({
    since: null,
    until: null,
  });
  const [preparationDateRange, setPreparationDateRange] = useState<{
    since: Date | null;
    until: Date | null;
  }>({
    since: null,
    until: null,
  });
  const [invoiceDateRange, setInvoiceDateRange] = useState<{
    since: Date | null;
    until: Date | null;
  }>({
    since: null,
    until: null,
  });
  const [code, setCode] = useState<string | null>(null);
  const [externalReference, setExternalReference] = useState<string | null>(
    null
  );
  const [selectedPos, setSelectedPos] = useState<{
    value: number;
    label: string;
    distributor: number;
  } | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [origin, setOrigin] = useState<string | null>(null);
  const [onlyFreeOrders, setOnlyFreeOrders] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  async function fetchOrders(withLoader = true) {
    try {
      if (withLoader) {
        setLoading(true);
      }

      const promiseDateSince = promiseDateRange?.since
        ? promiseDateRange.since.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined;
      const promiseDateUntil = promiseDateRange.until
        ? promiseDateRange.until.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined;
      const preparationDateSince = preparationDateRange.since
        ? preparationDateRange.since.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined;
      const preparationDateUntil = preparationDateRange.until
        ? preparationDateRange.until.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined;
      const invoiceDateSince = invoiceDateRange.since
        ? invoiceDateRange.since.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined;
      const invoiceDateUntil = invoiceDateRange.until
        ? invoiceDateRange.until.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined;

      const { data } = await listOrders({
        state: selectedStates,
        distributorId: selectedDistributor?.id,
        shippingService: selectedShippingService?.id,
        page,
        country,
        origin,
        code,
        externalReference,
        shippingAddressId: selectedPos?.value,
        promiseDateSince,
        promiseDateUntil,
        preparationDateSince,
        preparationDateUntil,
        invoiceDateSince,
        invoiceDateUntil,
        warehouse,
        onlyFreeOrders: onlyFreeOrders || undefined,
      });
      setOrders(data?.results);
      setCount(data?.count);
    } catch (error) {
      console.warn(error);
    } finally {
      if (withLoader) {
        setLoading(false);
      }
    }
  }

  async function fetchProducts(distributorId) {
    try {
      setLoadingProducts(true);
      const { data } = await listSaleableProducts({ distributorId });
      setProducts(data);
    } catch (error) {
      console.warn(error);
    } finally {
      setLoadingProducts(false);
    }
  }

  // Load points of sale (PDV) for the async select. If no distributor is
  // selected the query hits every distributor, so require a minimum input to
  // avoid loading the whole list; when a distributor is selected its PDVs are
  // returned even with an empty input.
  async function loadPosOptions(inputValue: string) {
    const search = inputValue.trim();
    if (!selectedDistributor && search.length < 2) {
      return [];
    }
    try {
      const { data } = await searchDistributorShippingAddresses({
        distributorId: selectedDistributor?.id,
        search: search || undefined,
      });
      return data.map((pos) => ({
        value: pos.id,
        label: pos.name,
        distributor: pos.distributor,
      }));
    } catch (error) {
      console.warn(error);
      return [];
    }
  }

  const fetchDependencies = [
    selectedDistributor,
    selectedShippingService,
    selectedStates,
    promiseDateRange,
    preparationDateRange,
    invoiceDateRange,
    code,
    externalReference,
    selectedPos,
    country,
    origin,
    warehouse,
    onlyFreeOrders,
  ];
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchOrders();
    }, DEBOUNCE_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [...fetchDependencies, page]);

  useEffect(() => {
    setPage(1);
    setCount(0);
  }, fetchDependencies);

  useEffect(() => {
    if (order?.distributor) {
      fetchProducts(order?.distributor);
    }
  }, [order?.distributor]);

  const fetchDistributors = async () => {
    try {
      const { data } = await listDistributors('active');
      setDistributors(data);
    } catch (error) {
      console.warn(error);
    }
  };

  useEffect(() => {
    fetchDistributors();
  }, []);

  function onCreateOrder() {
    setOrder(EMPTY_ORDER as OrderModel);
    setVisibleOrderModal(true);
  }

  async function createNewOrder(orderModel: OrderModel) {
    try {
      setLoading(true);
      setSelectedStates([OrderStateEnum.PROPOSAL]);
      const { data: newlyCreatedOrder } = await createOrder(orderModel);
      setOrder(newlyCreatedOrder);
      toast.success(intl.formatMessage({ id: 'b2bOrders.createSuccess' }), {
        position: toast.POSITION.TOP_RIGHT,
      });
      setVisibleOrderModal(false);
      setVisibleLinesModal(true);
      await fetchOrders();
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          intl.formatMessage({ id: 'b2bOrders.createError' })
        )
      );
      console.warn(error);
    } finally {
      setLoading(false);
    }
  }

  function onParseEdi() {
    setVisibleParseEdiModal(true);
  }

  async function updateExistingOrder(orderModel: OrderModel) {
    try {
      await updateOrder(orderModel);
      setVisibleOrderModal(false);
      fetchOrders();
      setOrder(undefined);
      toast.success(intl.formatMessage({ id: 'b2bOrders.updateSuccess' }));
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          intl.formatMessage({ id: 'b2bOrders.updateError' })
        )
      );
      console.warn(error);
    }
  }

  function onSaveCreatedOrder(order: OrderModel) {
    if (order.id) {
      updateExistingOrder(order);
    } else {
      createNewOrder(order);
    }
  }

  function onEditOrder(order: OrderModel) {
    setOrder(order);
    setVisibleOrderModal(true);
  }

  function onEditLines(order: OrderModel) {
    setOrder(order);
    setVisibleLinesModal(true);
  }

  async function onCloseLinesModal(shippingFee: number) {
    setVisibleLinesModal(false);
    if (order && order.shipping_fee !== shippingFee) {
      try {
        await updateOrder({ ...order, shipping_fee: shippingFee });
        fetchOrders(false);
        toast.success(intl.formatMessage({ id: 'b2bOrders.updateSuccess' }));
      } catch (error) {
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: 'b2bOrders.updateError' })
          )
        );
        console.warn(error);
      }
    }
    setOrder(undefined);
  }

  async function onAddLine(line: OrderLineModel) {
    try {
      await createOrderLine(line);
      const { data: updatedOrder } = await retrieveOrder(order.id);
      setOrder(updatedOrder);
      fetchOrders(false);
      toast.success(intl.formatMessage({ id: 'b2bOrders.createLineSuccess' }));
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'b2bOrders.createLineError' }));
      console.warn(error);
    }
  }

  async function onRemoveLine(line: OrderLineModel) {
    const hasConfirmed = window.confirm(
      `${intl.formatMessage({ id: 'b2bOrders.deleteLineConfirmation' })}\r\n${line.units} x ${
        line.name
      }`
    );
    if (!hasConfirmed) {
      return;
    }
    try {
      await removeOrderLine(line);
      const { data: updatedOrder } = await retrieveOrder(order.id);
      setOrder(updatedOrder);
      fetchOrders(false);
      toast.success(intl.formatMessage({ id: 'b2bOrders.deleteLineSuccess' }));
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'b2bOrders.deleteLineError' }));
      console.warn(error);
    }
  }

  async function onUpdateLineUnits(line: OrderLineModel, newUnits: number) {
    try {
      await updateOrderLine(line.id, { units: newUnits });
      const { data: updatedOrder } = await retrieveOrder(order.id);
      setOrder(updatedOrder);
      fetchOrders(false);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'b2bOrders.updateLineError' }));
      console.warn(error);
    }
  }

  useEffect(() => {
    if (order?.id) {
      const updatedOrder = orders.find((o) => o.id === order.id);
      if (updatedOrder) {
        setOrder(updatedOrder);
      }
    }
  }, [orders, order?.id]);

  function onGenerateProforma(order: OrderModel) {
    setOrder(order);
    setVisibleInvoiceModal(true);
  }

  function onGenerateDeliveryNote(order: OrderModel) {
    setOrder(order);
    setVisibleDeliveryNoteModal(true);
  }

  async function onCreateDeliveryNote(
    date,
    deliveryNoteLines: DeliveryNoteLineRequestModel[]
  ) {
    try {
      const { data: deliveryNote } = await createDeliveryNote(order?.id, date);
      if (deliveryNote.id) {
        await bulkInsertDeliveryNoteLines(deliveryNote.id, deliveryNoteLines);
      } else {
        toast.error(
          intl.formatMessage({ id: 'b2bOrders.createDeliveryNoteError' })
        );
        return;
      }

      setVisibleDeliveryNoteModal(false);
      toast.success(
        intl.formatMessage(
          { id: 'b2bOrders.createDeliveryNoteSuccess' },
          {
            code: deliveryNote.code,
          }
        )
      );
      history.push(`/delivery-notes?deliveryNote=${deliveryNote.id}`);
    } catch (error) {
      toast.error(
        intl.formatMessage({ id: 'b2bOrders.createDeliveryNoteError' })
      );
      console.warn(error);
    }
  }

  function onCreatedOrder() {
    setVisibleParseEdiModal(false);
    fetchOrders();
  }

  async function onUpdateState(order: OrderModel, state: OrderStateEnum) {
    try {
      await updateOrder({
        ...order,
        state,
      });
      fetchOrders(false);
      toast.success(intl.formatMessage({ id: 'b2bOrders.updateStateSuccess' }));
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          intl.formatMessage({ id: 'b2bOrders.updateStateError' })
        )
      );
      console.warn(error);
    }
  }

  async function onPerformBulkAction(
    action: BulkActionsEnum,
    orders: number[]
  ) {
    const promiseDateSince = promiseDateRange?.since
      ? promiseDateRange.since.toLocaleDateString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : undefined;
    const promiseDateUntil = promiseDateRange?.until
      ? promiseDateRange.until.toLocaleDateString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : undefined;
    const preparationDateSince = preparationDateRange?.since
      ? preparationDateRange.since.toLocaleDateString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : undefined;
    const preparationDateUntil = preparationDateRange?.until
      ? preparationDateRange.until.toLocaleDateString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : undefined;
    const invoiceDateSince = invoiceDateRange?.since
      ? invoiceDateRange.since.toLocaleDateString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : undefined;
    const invoiceDateUntil = invoiceDateRange?.until
      ? invoiceDateRange.until.toLocaleDateString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : undefined;

    return performBulkAction({
      action,
      orders,
      country,
      origin,
      code,
      externalReference,
      state: selectedStates.length ? selectedStates.join(',') : undefined,
      shippingService: selectedShippingService?.id,
      promiseDateSince,
      promiseDateUntil,
      preparationDateSince,
      preparationDateUntil,
      invoiceDateSince,
      invoiceDateUntil,
      distributor: selectedDistributor?.id,
      warehouse,
      onlyFreeOrders: onlyFreeOrders || undefined,
    });
  }

  function onEditComments(order: OrderModel) {
    setOrder(order);
    setVisibleCommentsModal(true);
  }

  async function onSaveComments() {
    setVisibleCommentsModal(false);
    fetchOrders(false);
  }

  async function fetchShippingServices() {
    try {
      const { data } = await listShippingServices();
      setShippingServices(data);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    fetchShippingServices();
  }, []);

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'b2bOrders.title' })}
      </PageTitle>
      <div className="row">
        <div className="col-12">
          <OrderList
            displayBulkActions={true}
            orders={orders}
            products={products}
            loading={loading}
            page={page}
            count={count}
            onPageChange={setPage}
            onCreateOrder={onCreateOrder}
            onParseEdi={onParseEdi}
            onEditOrder={onEditOrder}
            onEditLines={onEditLines}
            onGenerateProforma={onGenerateProforma}
            onGenerateDeliveryNote={onGenerateDeliveryNote}
            onUpdateState={onUpdateState}
            onEditComments={onEditComments}
            bulkActions={BULK_ACTIONS}
            onBulkAction={onPerformBulkAction}
          >
            <main className="d-flex justify-content-start flex-wrap gap-4">
              <div className="form-group w-300px d-inline-block">
                <label className="form-label mb-2">
                  {intl.formatMessage({ id: 'b2bOrders.filterByDistributor' })}
                </label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder={intl.formatMessage({
                    id: 'b2bOrders.anyDistributor',
                  })}
                  styles={{
                    control: (provided) =>
                      ({
                        ...provided,
                        height: '42px',
                      }) as any,
                  }}
                  options={[
                    {
                      value: null,
                      label: intl.formatMessage({
                        id: 'b2bOrders.anyDistributor',
                      }),
                    },
                    ...distributors.map(
                      (distributor) =>
                        ({
                          value: distributor.id,
                          label: distributor.name,
                        }) as any
                    ),
                  ]}
                  value={
                    selectedDistributor
                      ? {
                          value: selectedDistributor?.id,
                          label: selectedDistributor?.name,
                        }
                      : null
                  }
                  onChange={(option: any) => {
                    const distributor = distributors.find(
                      (d) => d.id == option.value
                    );

                    setSelectedDistributor(distributor);
                    // Reset the PDV if it no longer belongs to the selected
                    // distributor (keep it when clearing to "any distributor").
                    if (
                      distributor &&
                      selectedPos &&
                      selectedPos.distributor !== distributor.id
                    ) {
                      setSelectedPos(null);
                    }
                  }}
                />
              </div>
              <div className="form-group w-300px d-inline-block">
                <label className="form-label mb-2">
                  {intl.formatMessage({ id: 'b2bOrders.filterByPosName' })}
                </label>
                <AsyncSelect
                  // Remount when the distributor changes so cached options and
                  // default results are reloaded for the new scope.
                  key={selectedDistributor?.id ?? 'all'}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                  cacheOptions
                  defaultOptions={!!selectedDistributor}
                  placeholder={intl.formatMessage({
                    id: 'b2bOrders.posNamePlaceholder',
                  })}
                  loadingMessage={() =>
                    intl.formatMessage({ id: 'b2bOrders.posNameLoading' })
                  }
                  noOptionsMessage={() =>
                    intl.formatMessage({ id: 'b2bOrders.posNameNoOptions' })
                  }
                  styles={{
                    control: (provided) =>
                      ({
                        ...provided,
                        height: '42px',
                      }) as any,
                  }}
                  loadOptions={loadPosOptions}
                  value={selectedPos}
                  onChange={(option: any) => {
                    setSelectedPos(option);
                    // Selecting a PDV pins its (single) distributor.
                    if (option) {
                      const distributor = distributors.find(
                        (d) => d.id === option.distributor
                      );
                      if (distributor) {
                        setSelectedDistributor(distributor);
                      }
                    }
                  }}
                />
              </div>
              <div className="form-group w-300px d-inline-block">
                <label className="form-label mb-2">
                  {intl.formatMessage({
                    id: 'b2bOrders.filterByShippingService',
                  })}
                </label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder={intl.formatMessage({
                    id: 'b2bOrders.anyShippingService',
                  })}
                  styles={{
                    control: (provided) =>
                      ({
                        ...provided,
                        height: '42px',
                      }) as any,
                  }}
                  options={[
                    {
                      value: ANY_SERVICE,
                      label: intl.formatMessage({
                        id: 'b2bOrders.anyShippingService',
                      }),
                    },
                    ...shippingServices.map((service) => ({
                      value: service.id,
                      label: service.name,
                    })),
                  ]}
                  value={
                    selectedShippingService
                      ? {
                          value: selectedShippingService?.id,
                          label: selectedShippingService?.name,
                        }
                      : null
                  }
                  onChange={(option: any) => {
                    const shippingService = shippingServices.find(
                      (s) => s.id == option.value
                    );

                    setSelectedShippingService(shippingService);
                  }}
                />
              </div>
              <div className="form-group w-300px d-inline-block">
                <label className="form-label mb-2">
                  {intl.formatMessage({ id: 'b2bOrders.filterByState' })}
                </label>
                <MultiSelect
                  className="multi-select"
                  overrideStrings={getMultiSelectStrings(intl)}
                  labelledBy="Select"
                  disableSearch={true}
                  options={Object.values(OrderStateEnum).map((state) => ({
                    value: state,
                    label: state,
                  }))}
                  value={selectedStates.map((state) => ({
                    value: state,
                    label: state,
                  }))}
                  onChange={(options) => {
                    setSelectedStates(options.map(({ value }) => value));
                  }}
                  valueRenderer={(selected) =>
                    selected.length
                      ? selected.map(({ label }) => label).join(', ')
                      : intl.formatMessage({ id: 'b2bOrders.anyState' })
                  }
                />
              </div>
              <div className="form-group w-180px d-inline-block">
                <label className="form-label mb-2">
                  {intl.formatMessage({ id: 'b2bOrders.filterByCountry' })}
                </label>
                <select
                  className="form-control form-select"
                  value={country}
                  onChange={(e) => setCountry(e.target.value || null)}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'b2bOrders.anyCountry' })}
                  </option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>
                      {COUNTRY_NAMES[country]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group w-180px d-inline-block">
                <label className="form-label mb-2">
                  {intl.formatMessage({ id: 'b2bOrders.filterByOrigin' })}
                </label>
                <select
                  className="form-control form-select"
                  value={origin || ''}
                  onChange={(e) => setOrigin(e.target.value || null)}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'b2bOrders.anyOrigin' })}
                  </option>
                  {Object.values(OrderOriginEnum).map((originValue) => (
                    <option key={originValue} value={originValue}>
                      {intl.formatMessage({
                        id: ORIGIN_LABEL_IDS[originValue],
                      })}
                    </option>
                  ))}
                  <option value="none">
                    {intl.formatMessage({ id: 'b2bOrders.originUnspecified' })}
                  </option>
                </select>
              </div>
              <div className="form-group w-180px d-inline-block">
                <label className="form-label mb-2">
                  {intl.formatMessage({ id: 'b2bOrders.filterByCode' })}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={intl.formatMessage({
                    id: 'b2bOrders.codePlaceholder',
                  })}
                  value={code || ''}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <div className="form-group w-300px d-inline-block">
                <label className="form-label mb-2">
                  {intl.formatMessage({
                    id: 'b2bOrders.filterByExternalReference',
                  })}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={intl.formatMessage({
                    id: 'b2bOrders.externalReferencePlaceholder',
                  })}
                  value={externalReference || ''}
                  onChange={(e) => setExternalReference(e.target.value)}
                />
              </div>
              <div className="form-group w-180px d-inline-block">
                <DateRangeSelector
                  label={intl.formatMessage({
                    id: 'b2bOrders.filterByPreparationDate',
                  })}
                  onSelected={(since, until) =>
                    setPreparationDateRange({ since, until })
                  }
                  initialSince={preparationDateRange.since}
                  initialUntil={preparationDateRange.until}
                  color="info"
                />
              </div>
              <div className="form-group w-180px d-inline-block">
                <DateRangeSelector
                  label={intl.formatMessage({
                    id: 'operationOrders.filterByPromiseDate',
                  })}
                  onSelected={(since, until) =>
                    setPromiseDateRange({ since, until })
                  }
                  initialSince={promiseDateRange.since}
                  initialUntil={promiseDateRange.until}
                />
              </div>
              <div className="form-group w-180px d-inline-block">
                <DateRangeSelector
                  label={intl.formatMessage({
                    id: 'operationOrders.filterByBillingDate',
                  })}
                  onSelected={(since, until) =>
                    setInvoiceDateRange({ since, until })
                  }
                  initialSince={invoiceDateRange.since}
                  initialUntil={invoiceDateRange.until}
                />
              </div>
              <WarehouseFilter value={warehouse} onChange={setWarehouse} />
              <div className="form-group d-inline-block">
                <label className="form-label mb-2 d-block">
                  {intl.formatMessage({ id: 'b2bOrders.filterByFreeOrders' })}
                </label>
                <div
                  className="d-flex align-items-center"
                  style={{ height: '42px' }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    id="onlyFreeOrders"
                    checked={onlyFreeOrders}
                    onChange={(e) => setOnlyFreeOrders(e.target.checked)}
                  />
                </div>
              </div>
            </main>
          </OrderList>
        </div>
      </div>
      <ModalOrderForm
        visible={visibleOrderModal}
        onCancel={() => setVisibleOrderModal(false)}
        onSaveOrder={onSaveCreatedOrder}
        order={order}
        distributors={distributors}
      />
      <ModalOrderLines
        visible={visibleLinesModal}
        loading={loading}
        loadingProducts={loadingProducts}
        onClose={onCloseLinesModal}
        order={order}
        products={products}
        onAddLine={onAddLine}
        onRemoveLine={onRemoveLine}
        onUpdateLineUnits={onUpdateLineUnits}
      />
      <ModalInvoiceForm
        visible={visibleInvoiceModal}
        onClose={() => setVisibleInvoiceModal(false)}
        order={order}
        invoiceKind={InvoiceKind.Proforma}
      />
      <ModalDeliveryNoteForm
        visible={visibleDeliveryNoteModal}
        onClose={() => setVisibleDeliveryNoteModal(false)}
        onCreateDeliveryNote={onCreateDeliveryNote}
        order={order}
      />
      <ModalEdiForm
        visible={visibleParseEdiModal}
        onClose={() => setVisibleParseEdiModal(false)}
        onCreated={onCreatedOrder}
        distributors={distributors}
      />
      <ModalComments
        visible={visibleCommentsModal}
        onClose={() => setVisibleCommentsModal(false)}
        onSaved={onSaveComments}
        order={order}
      />
    </>
  );
};

export { B2BOrdersPage };
